import React from "react";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import PopupAnchor from "../../components/popup-anchor";
import { Container, CssContainer } from "../../components/container";
import ManageDataTabs from "../../components/manage-data-tabs";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import ServiceFactory from "../../service";
import account from "../../data/account";
import { hasTenantFeature, manageDataLegacyHere } from "../../functions/tenant-features";
import { Empty } from "../../components/analytics-kit";
import DataShell from "../manage-data/shell";
import RulesKit from "../manage-data/rules-kit";

/*
 * Configuration Rules tab (PRODUCT_CONFIG_RULES.md §5 — the lifecycle-editor
 * pattern applied to config parsing). Owner/admin: the grammar that drives
 * Product Configurations becomes an editable rule list with an AI
 * "Suggest rules" adapter, a pairing-strategy selector, and the §3 coverage
 * preview so a tenant can SEE what their rules match before saving.
 *
 * Contracts (AnalyticsService ProductConfigurationController, live-probed):
 *  GET  rules            → { rules, version, updatedBy, updatedAt, history[] }
 *  PUT  rules            → body IS the grammar JSON; server validates; 403 non-owner
 *  POST rules/preview    → { rules?, startDate?, endDate? } → coverage report
 *  POST rules/suggest    → {} → { rules:[{kind, skuPattern|descPattern, rationale}], note }
 *  POST rules/rebuild    → { startDate?, endDate? } → { written, windowStart, windowEnd }
 *                          (default trailing 90 days; nightly job covers full history)
 * linked_field accepts a whitelisted sales column (coupon, customer_id,
 * store_id, order_status) or metadata:<key>.
 */

// Suggested kinds for the combobox — NOT a closed list. The rollup engine
// classifies on whatever kind string a rule carries (cushion, pillow, cover,
// slipcover, glide, nailhead, …), so the kind field is free-entry: pick one of
// these or type your own. `frame` is the style anchor; `ignore` drops the line.
const KINDS = ["frame", "cushion", "fabric", "finish", "leather", "pillow", "cover", "slipcover", "glide", "nailhead", "ignore"];
const KIND_COLORS = {
  frame: "#ff6358",
  cushion: "#2f80ed",
  fabric: "#78d237",
  finish: "#ffd246",
  leather: "#aa46be",
  pillow: "#12b5b0",
  cover: "#e0731d",
  slipcover: "#8a6d3b",
  glide: "#5b6b8c",
  nailhead: "#b25e09",
  ignore: "#9aa4b2",
  unmatched: "#e0e0e0"
};
const STRATEGIES = [["same_order", "Same order — components pair with frames on the same order"], ["linked_field", "Linked field — pair by an explicit link column in the sales feed"], ["none", "None — components aggregate standalone (no styles-sold-on view)"]];
function svc(api) {
  const Service = ServiceFactory.getType(api, "product");
  return new Service();
}
const isAdmin = () => {
  const groups = ((account.get() || {}).groups || []).map(g => String(g).toLowerCase());
  return ["owner", "administrator", "root"].some(g => groups.includes(g));
};
const num = v => (Number(v) || 0).toLocaleString("en-US");
export default function ConfigRulesView() {
  const [meta, setMeta] = React.useState(null); // {version, updatedBy, updatedAt, history}
  const [rules, setRules] = React.useState(null); // editable lineRules array
  const [pairing, setPairing] = React.useState({
    strategy: "same_order",
    linkedField: ""
  });
  const [seriesKey, setSeriesKey] = React.useState(""); // SeriesKey.Pattern — group 1 = the series key
  const [error, setError] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [report, setReport] = React.useState(null);
  const [drafts, setDrafts] = React.useState(null); // suggest results
  const [note, setNote] = React.useState(null);
  const [confirmRebuild, setConfirmRebuild] = React.useState(false);
  const baseRef = React.useRef({}); // the loaded grammar, so the kit's save keeps fields the editor doesn't show

  const load = React.useCallback(() => {
    svc("/product/configurations/rules").getAsync().then(d => {
      const g = d.rules || {
        mode: "order_lines",
        componentAttach: "order",
        lineRules: []
      };
      setMeta(d);
      baseRef.current = g;
      setRules((g.lineRules || []).map(r => ({
        ...r
      })));
      setPairing({
        strategy: g.pairing && g.pairing.strategy || "same_order",
        linkedField: g.pairing && g.pairing.linkedField || ""
      });
      setSeriesKey(g.SeriesKey && g.SeriesKey.Pattern || g.seriesKey && g.seriesKey.pattern || "");
      setDirty(false);
    }).catch(() => setError(true));
  }, []);
  React.useEffect(() => {
    load();
  }, [load]);
  const grammar = () => {
    const g = {
      mode: "order_lines",
      componentAttach: "order",
      lineRules: rules.map(r => {
        const out = {
          kind: r.kind
        };
        if (r.skuPattern) out.skuPattern = r.skuPattern;
        if (r.descPattern) out.descPattern = r.descPattern;
        return out;
      })
    };
    if (pairing.strategy !== "same_order" || pairing.linkedField) {
      g.pairing = {
        strategy: pairing.strategy
      };
      if (pairing.strategy === "linked_field") g.pairing.linkedField = pairing.linkedField;
    }
    if (seriesKey && seriesKey.trim()) g.SeriesKey = {
      Pattern: seriesKey.trim()
    };
    return g;
  };
  const mutate = fn => {
    fn();
    setRules(r => [...r]);
    setDirty(true);
    setNote(null);
  };
  const move = (i, d) => mutate(() => {
    const t = rules[i];
    rules[i] = rules[i + d];
    rules[i + d] = t;
  });
  const editRule = (i, patch) => mutate(() => {
    rules[i] = {
      ...rules[i],
      ...patch
    };
  });
  const removeRule = i => mutate(() => rules.splice(i, 1));
  const addRule = r => mutate(() => rules.push(r || {
    kind: "frame",
    skuPattern: ""
  }));
  const preview = () => {
    setBusy(true);
    setNote(null);
    svc("/product/configurations/rules/preview").postAsync({
      rules: grammar()
    }).then(rep => {
      setReport(rep);
      setBusy(false);
    }).catch(e => {
      setNote(msgOf(e, "Preview failed — check that every pattern compiles."));
      setBusy(false);
    });
  };
  const suggest = () => {
    setBusy(true);
    setNote(null);
    svc("/product/configurations/rules/suggest").postAsync({}).then(d => {
      setDrafts(d && d.rules || []);
      setBusy(false);
    }).catch(e => {
      setNote(msgOf(e, "Suggest failed — try again."));
      setBusy(false);
    });
  };
  const save = () => {
    setBusy(true);
    setNote(null);
    svc("/product/configurations/rules").putAsync(grammar()).then(d => {
      setBusy(false);
      setNote(`Saved — version ${d && d.version}. Rebuild to apply to existing history.`);
      load();
    }).catch(e => {
      setNote(msgOf(e, "Save failed — rules didn't validate."));
      setBusy(false);
    });
  };
  const rebuild = () => {
    setBusy(true);
    setConfirmRebuild(false);
    setNote(null);
    svc("/product/configurations/rules/rebuild").postAsync({}).then(d => {
      setBusy(false);
      setNote(`Rebuilt ${num(d && d.written)} rows for ${String(d && d.windowStart || "").slice(0, 10)} → ${String(d && d.windowEnd || "").slice(0, 10)}. The nightly job covers full history on its next pass.`);
    }).catch(e => {
      setNote(msgOf(e, "Rebuild failed."));
      setBusy(false);
    });
  };
  const revert = h => {
    baseRef.current = h || {};
    setRules((h && h.lineRules || []).map(r => ({
      ...r
    })));
    setPairing({
      strategy: h.pairing && h.pairing.strategy || "same_order",
      linkedField: h.pairing && h.pairing.linkedField || ""
    });
    setSeriesKey(h.SeriesKey && h.SeriesKey.Pattern || h.seriesKey && h.seriesKey.pattern || "");
    setDirty(true);
    setNote("Prior version loaded into the editor — review, Preview, then Save.");
  };
  const msgOf = (e, fallback) => {
    try {
      const b = JSON.parse(e.message || "{}");
      if (b.error) return b.error;
    } catch (x) {/* not json */}
    return e && e.error || fallback;
  };

  // ---- Kit path (new layout). The legacy layout above keeps grammar()/save()/rebuild() unchanged.
  // fullGrammar keeps every grammar field the editor doesn't surface (variant rules, metadata options,
  // per-rule flags such as identityFromDesc) so saving from the new layout never drops them.
  const cleanRule = r => {
    const {
      skuPattern,
      descPattern,
      ...rest
    } = r;
    const out = {
      ...rest
    };
    Object.keys(out).forEach(k => {
      if (out[k] === undefined) delete out[k];
    });
    if (skuPattern) out.skuPattern = skuPattern;
    if (descPattern) out.descPattern = descPattern;
    return out;
  };
  const fullGrammar = () => {
    const base = {
      ...(baseRef.current || {})
    };
    delete base.pairing;
    delete base.Pairing;
    delete base.SeriesKey;
    delete base.seriesKey;
    const g = {
      ...base,
      ...grammar()
    };
    g.lineRules = rules.map(cleanRule);
    return g;
  };
  const kitPreview = () => svc("/product/configurations/rules/preview").postAsync({
    rules: fullGrammar()
  });
  // One rule on its own against recent orders: matched = lines total minus lines it left unmatched.
  const kitPreviewRule = rule => svc("/product/configurations/rules/preview").postAsync({
    rules: {
      mode: "order_lines",
      componentAttach: "order",
      lineRules: [cleanRule(rule)]
    }
  }).then(rep => rep && !rep.empty ? {
    matched: Math.max(0, (Number(rep.linesTotal) || 0) - (Number(rep.unmatched) || 0)),
    windowStart: rep.windowStart,
    windowEnd: rep.windowEnd
  } : {
    matched: null
  });
  const kitSuggest = () => svc("/product/configurations/rules/suggest").postAsync({});
  const kitSave = () => svc("/product/configurations/rules").putAsync(fullGrammar());
  const kitRebuild = () => svc("/product/configurations/rules/rebuild").postAsync({});
  const legacy = manageDataLegacyHere();
  const SUB = "Configuration Rules: how sales lines classify into frames, fabrics and components, and how they pair";
  if (!hasTenantFeature("productConfigs")) {
    if (!legacy) return /*#__PURE__*/React.createElement(DataShell, {
      active: "/Data/Rules",
      subtitle: SUB
    }, /*#__PURE__*/React.createElement(Empty, null, "Product Configurations is not configured for this workspace."));
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view"
    }, /*#__PURE__*/React.createElement(ManageDataTabs, {
      active: "/Data/Rules"
    }), /*#__PURE__*/React.createElement(CssContainer, {
      cls: "margin-squeeze"
    }, /*#__PURE__*/React.createElement("p", {
      className: "module-empty"
    }, "Product Configurations isn't configured for this workspace.")));
  }
  if (!isAdmin()) {
    if (!legacy) return /*#__PURE__*/React.createElement(DataShell, {
      active: "/Data/Rules",
      subtitle: SUB
    }, /*#__PURE__*/React.createElement(Empty, null, "Configuration rules are available to workspace owners and administrators."));
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view"
    }, /*#__PURE__*/React.createElement(ManageDataTabs, {
      active: "/Data/Rules"
    }), /*#__PURE__*/React.createElement(CssContainer, {
      cls: "margin-squeeze"
    }, /*#__PURE__*/React.createElement("p", {
      className: "module-empty"
    }, "Configuration rules are available to workspace owners and administrators.")));
  }
  if (error) return legacy ? /*#__PURE__*/React.createElement("div", {
    className: "territory-view"
  }, /*#__PURE__*/React.createElement(ManageDataTabs, {
    active: "/Data/Rules"
  }), /*#__PURE__*/React.createElement(Error, null)) : /*#__PURE__*/React.createElement(DataShell, {
    active: "/Data/Rules",
    subtitle: SUB
  }, /*#__PURE__*/React.createElement(Error, null));
  if (!rules) return legacy ? /*#__PURE__*/React.createElement("div", {
    className: "territory-view"
  }, /*#__PURE__*/React.createElement(ManageDataTabs, {
    active: "/Data/Rules"
  }), /*#__PURE__*/React.createElement(Loader, null)) : /*#__PURE__*/React.createElement(DataShell, {
    active: "/Data/Rules",
    subtitle: SUB
  }, /*#__PURE__*/React.createElement(Loader, null));
  const totalClassified = report ? Object.values(report.classified || {}).reduce((a, v) => a + (Number(v) || 0), 0) : 0;
  const segs = report ? [...Object.entries(report.classified || {}), ["unmatched", report.unmatched || 0]] : [];
  if (!legacy) {
    return /*#__PURE__*/React.createElement(RulesKit, {
      meta,
      rules,
      pairing,
      setPairing,
      seriesKey,
      setSeriesKey,
      dirty,
      setDirty,
      report,
      setReport,
      drafts,
      setDrafts,
      note,
      setNote,
      revert,
      move,
      editRule,
      removeRule,
      addRule,
      load,
      msgOf,
      KIND_COLORS,
      num,
      segs,
      seriesHidden: hasTenantFeature("noProductSeries"),
      kitPreview,
      kitPreviewRule,
      kitSuggest,
      kitSave,
      kitRebuild
    });
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "territory-view config-rules-view"
  }, /*#__PURE__*/React.createElement(ManageDataTabs, {
    active: "/Data/Rules"
  }), /*#__PURE__*/React.createElement(CssContainer, {
    cls: "grid-button space-between margin-squeeze align-end"
  }, /*#__PURE__*/React.createElement("div", {
    className: "territory-detail-head"
  }, /*#__PURE__*/React.createElement("div", {
    className: "heading-label"
  }, "Configuration Rules")), /*#__PURE__*/React.createElement("div", {
    className: "territory-detail-head",
    style: {
      gap: 8
    }
  }, /*#__PURE__*/React.createElement("button", {
    className: "k-button",
    disabled: busy,
    onClick: preview
  }, "Preview these rules"), /*#__PURE__*/React.createElement("button", {
    className: "k-button k-primary",
    disabled: busy || !dirty,
    onClick: save
  }, "Save rules"), confirmRebuild ? /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: 13
    }
  }, "Re-parse recent sales with the saved rules?", " ", /*#__PURE__*/React.createElement("button", {
    className: "k-button",
    onClick: rebuild
  }, "Yes, rebuild"), " ", /*#__PURE__*/React.createElement("button", {
    className: "k-button",
    onClick: () => setConfirmRebuild(false)
  }, "Cancel")) : /*#__PURE__*/React.createElement("button", {
    className: "k-button",
    disabled: busy || dirty,
    title: dirty ? "Save first" : undefined,
    onClick: () => setConfirmRebuild(true)
  }, "Rebuild"))), note && /*#__PURE__*/React.createElement(CssContainer, {
    cls: "margin-squeeze"
  }, /*#__PURE__*/React.createElement("p", {
    className: "territory-hint",
    style: {
      marginTop: 0
    }
  }, note)), /*#__PURE__*/React.createElement(Container, {
    title: `Line rules — first match wins${meta && meta.version ? ` · v${meta.version}${meta.updatedBy ? ` by ${meta.updatedBy}` : ""}` : ""}`,
    css: "margin-squeeze"
  }, /*#__PURE__*/React.createElement("table", {
    className: "territory-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", {
    style: {
      width: 60
    }
  }, "Order"), /*#__PURE__*/React.createElement("th", null, "Classifies as"), /*#__PURE__*/React.createElement("th", null, "Applies to"), /*#__PURE__*/React.createElement("th", null, "Pattern"), /*#__PURE__*/React.createElement("th", {
    style: {
      width: 40
    }
  }))), /*#__PURE__*/React.createElement("tbody", null, rules.map((r, i) => {
    const appliesTo = r.descPattern != null && r.skuPattern == null ? "desc" : "sku";
    return /*#__PURE__*/React.createElement("tr", {
      key: i
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("button", {
      className: "rep-state-chip",
      disabled: i === 0,
      onClick: () => move(i, -1),
      title: "Move up"
    }, "\u2191"), /*#__PURE__*/React.createElement("button", {
      className: "rep-state-chip",
      disabled: i === rules.length - 1,
      onClick: () => move(i, 1),
      title: "Move down"
    }, "\u2193")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(PopupAnchor, null, anchor => /*#__PURE__*/React.createElement(ComboBox, {
      data: Array.from(new Set([...KINDS, ...rules.map(x => x.kind).filter(Boolean)])),
      value: r.kind,
      allowCustom: true,
      clearButton: false,
      placeholder: "frame, cushion, \u2026",
      style: {
        width: 150
      },
      popupSettings: anchor ? {
        appendTo: anchor
      } : undefined,
      onChange: e => editRule(i, {
        kind: (e.value || "").toString().trim().toLowerCase()
      })
    })), /*#__PURE__*/React.createElement("span", {
      style: {
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: 5,
        background: KIND_COLORS[r.kind] || "#ccc",
        marginLeft: 8
      }
    })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("select", {
      value: appliesTo,
      style: {
        padding: "4px 6px"
      },
      onChange: e => {
        const p = r.skuPattern != null ? r.skuPattern : r.descPattern || "";
        editRule(i, e.target.value === "sku" ? {
          skuPattern: p,
          descPattern: undefined
        } : {
          descPattern: p,
          skuPattern: undefined
        });
      }
    }, /*#__PURE__*/React.createElement("option", {
      value: "sku"
    }, "SKU"), /*#__PURE__*/React.createElement("option", {
      value: "desc"
    }, "Description"))), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("input", {
      value: appliesTo === "sku" ? r.skuPattern || "" : r.descPattern || "",
      onChange: e => editRule(i, appliesTo === "sku" ? {
        skuPattern: e.target.value
      } : {
        descPattern: e.target.value
      }),
      spellCheck: false,
      style: {
        width: "100%",
        fontFamily: "monospace",
        fontSize: 12.5,
        padding: "5px 8px",
        border: "1px solid var(--plk-border,#ddd)",
        borderRadius: 4
      }
    })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("button", {
      className: "rep-state-chip",
      onClick: () => removeRule(i),
      title: "Delete rule"
    }, "\u2715")));
  }))), /*#__PURE__*/React.createElement("div", {
    style: {
      marginTop: 8,
      display: "flex",
      gap: 8
    }
  }, /*#__PURE__*/React.createElement("button", {
    className: "k-button",
    onClick: () => addRule()
  }, "Add rule"), /*#__PURE__*/React.createElement("button", {
    className: "k-button",
    disabled: busy,
    onClick: suggest
  }, "Suggest rules")), /*#__PURE__*/React.createElement("p", {
    className: "territory-hint"
  }, "Rules run top-down against each sales line \u2014 the first matching rule classifies it. Patterns are case-insensitive regular expressions; lines no rule matches are reported as unmatched in the preview, never guessed.")), drafts && /*#__PURE__*/React.createElement(Container, {
    title: "Suggested rules \u2014 drafts, nothing saves until you accept",
    css: "margin-squeeze"
  }, drafts.length === 0 ? /*#__PURE__*/React.createElement("p", {
    className: "module-empty"
  }, "No suggestions for this catalog's line shapes.") : /*#__PURE__*/React.createElement("table", {
    className: "territory-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Classifies as"), /*#__PURE__*/React.createElement("th", null, "Pattern"), /*#__PURE__*/React.createElement("th", null, "Why"), /*#__PURE__*/React.createElement("th", {
    style: {
      width: 90
    }
  }))), /*#__PURE__*/React.createElement("tbody", null, drafts.map((d, i) => /*#__PURE__*/React.createElement("tr", {
    key: i
  }, /*#__PURE__*/React.createElement("td", null, d.kind), /*#__PURE__*/React.createElement("td", {
    style: {
      fontFamily: "monospace",
      fontSize: 12.5
    }
  }, d.skuPattern || d.descPattern, d.descPattern ? " (desc)" : ""), /*#__PURE__*/React.createElement("td", {
    style: {
      fontSize: 12.5
    }
  }, d.rationale), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("button", {
    className: "rep-state-chip",
    onClick: () => {
      addRule({
        kind: d.kind,
        skuPattern: d.skuPattern,
        descPattern: d.descPattern
      });
      setDrafts(drafts.filter((x, j) => j !== i));
    }
  }, "Accept")))))), /*#__PURE__*/React.createElement("div", {
    style: {
      marginTop: 8,
      display: "flex",
      gap: 8
    }
  }, drafts.length > 0 && /*#__PURE__*/React.createElement("button", {
    className: "k-button",
    onClick: () => {
      drafts.forEach(d => addRule({
        kind: d.kind,
        skuPattern: d.skuPattern,
        descPattern: d.descPattern
      }));
      setDrafts(null);
    }
  }, "Accept all"), /*#__PURE__*/React.createElement("button", {
    className: "k-button",
    onClick: () => setDrafts(null)
  }, "Dismiss"))), /*#__PURE__*/React.createElement(Container, {
    title: "Component pairing",
    css: "margin-squeeze"
  }, STRATEGIES.map(([key, label]) => /*#__PURE__*/React.createElement("label", {
    key: key,
    style: {
      display: "block",
      margin: "6px 0",
      fontSize: 13.5
    }
  }, /*#__PURE__*/React.createElement("input", {
    type: "radio",
    name: "pairing",
    checked: pairing.strategy === key,
    onChange: () => {
      setPairing({
        ...pairing,
        strategy: key
      });
      setDirty(true);
    },
    style: {
      marginRight: 8
    }
  }), label)), pairing.strategy === "linked_field" && /*#__PURE__*/React.createElement("div", {
    style: {
      margin: "8px 0 0 24px"
    }
  }, /*#__PURE__*/React.createElement("input", {
    value: pairing.linkedField,
    placeholder: "coupon \xB7 customer_id \xB7 store_id \xB7 order_status \xB7 metadata:<key>",
    onChange: e => {
      setPairing({
        ...pairing,
        linkedField: e.target.value
      });
      setDirty(true);
    },
    style: {
      width: 380,
      fontFamily: "monospace",
      fontSize: 12.5,
      padding: "5px 8px",
      border: "1px solid var(--plk-border,#ddd)",
      borderRadius: 4
    }
  }), /*#__PURE__*/React.createElement("p", {
    className: "territory-hint",
    style: {
      marginTop: 4
    }
  }, "A sales-feed column both the frame and component lines share (whitelisted columns, or ", /*#__PURE__*/React.createElement("code", null, "metadata:<key>"), " for a value inside the line's metadata). The preview reports how many components actually pair on it."))), /*#__PURE__*/React.createElement(Container, {
    title: "Series key (advanced)",
    css: "margin-squeeze"
  }, /*#__PURE__*/React.createElement("p", {
    className: "territory-hint",
    style: {
      marginTop: 0
    }
  }, "Controls how Product Explorer groups SKUs into a ", /*#__PURE__*/React.createElement("b", null, "Series"), ". Leave blank to auto-detect. To override, enter a regular expression whose ", /*#__PURE__*/React.createElement("b", null, "capture group 1"), " is the series key \u2014 it's matched against each ", /*#__PURE__*/React.createElement("code", null, "product_id"), ". Use this when one model ships under material-prefixed SKUs that should roll up together."), /*#__PURE__*/React.createElement("input", {
    value: seriesKey,
    placeholder: "^[A-Za-z]*([0-9]+)",
    onChange: e => {
      setSeriesKey(e.target.value);
      setDirty(true);
      setNote(null);
    },
    style: {
      width: 380,
      fontFamily: "monospace",
      fontSize: 12.5,
      padding: "5px 8px",
      border: "1px solid var(--plk-border,#ddd)",
      borderRadius: 4
    }
  }), /*#__PURE__*/React.createElement("p", {
    className: "territory-hint",
    style: {
      marginTop: 4
    }
  }, "Example \u2014 ", /*#__PURE__*/React.createElement("code", null, "^[A-Za-z]*([0-9]+)"), ": ", /*#__PURE__*/React.createElement("code", null, "C3583-03"), ", ", /*#__PURE__*/React.createElement("code", null, "L3583-01"), " and", /*#__PURE__*/React.createElement("code", null, " 3583-79"), " all group under series ", /*#__PURE__*/React.createElement("b", null, "3583"), " (the leading letters are dropped, the digits are the key). Group 1 is required."), /*#__PURE__*/React.createElement("p", {
    className: "territory-hint",
    style: {
      marginTop: 8,
      paddingTop: 8,
      borderTop: "1px solid var(--plk-border,#eee)"
    }
  }, /*#__PURE__*/React.createElement("b", null, "Series tab:"), " ", hasTenantFeature("noProductSeries") ? /*#__PURE__*/React.createElement(React.Fragment, null, "Hidden \u2014 this workspace's catalog has no product series, so Product Explorer shows only Products and Configurations.") : /*#__PURE__*/React.createElement(React.Fragment, null, "Shown in Product Explorer."), " ", "Managed by your Plunk administrator.")), report && !report.empty && /*#__PURE__*/React.createElement(Container, {
    title: `Coverage preview — ${String(report.windowStart || "").slice(0, 10)} → ${String(report.windowEnd || "").slice(0, 10)}`,
    css: "margin-squeeze"
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      height: 18,
      borderRadius: 9,
      overflow: "hidden",
      margin: "4px 0 6px"
    }
  }, segs.filter(([, v]) => Number(v) > 0).map(([k, v]) => /*#__PURE__*/React.createElement("div", {
    key: k,
    title: `${k}: ${num(v)}`,
    style: {
      width: `${100 * Number(v) / Math.max(1, report.linesTotal)}%`,
      background: KIND_COLORS[k] || "#ccc"
    }
  }))), /*#__PURE__*/React.createElement("p", {
    style: {
      fontSize: 13,
      margin: "0 0 10px"
    }
  }, num(report.linesTotal), " sales lines \xB7 ", segs.filter(([, v]) => Number(v) > 0).map(([k, v]) => `${k} ${num(v)}`).join(" · ")), /*#__PURE__*/React.createElement("p", {
    style: {
      fontSize: 13,
      margin: "0 0 10px"
    }
  }, /*#__PURE__*/React.createElement("b", null, "Pairing (", report.strategy, "):"), " ", num(report.componentLines), " component lines \u2192 ", num(report.pairedComponentLines), " paired \xB7 ", num(report.distinctComponents), " distinct components \xB7 ", num(report.styles), " styles \xB7 ", num(report.pairs), " style-component pairs"), report.unpairedReason && /*#__PURE__*/React.createElement("p", {
    className: "territory-hint",
    style: {
      borderLeft: "3px solid #ffd246",
      paddingLeft: 10
    }
  }, report.unpairedReason), (report.topUnmatchedShapes || []).length > 0 && /*#__PURE__*/React.createElement("table", {
    className: "territory-table",
    style: {
      marginTop: 8
    }
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Unmatched shape"), /*#__PURE__*/React.createElement("th", {
    className: "num"
  }, "Lines"), /*#__PURE__*/React.createElement("th", null, "Sample"))), /*#__PURE__*/React.createElement("tbody", null, report.topUnmatchedShapes.map((s, i) => /*#__PURE__*/React.createElement("tr", {
    key: i
  }, /*#__PURE__*/React.createElement("td", {
    style: {
      fontFamily: "monospace"
    }
  }, s.shape), /*#__PURE__*/React.createElement("td", {
    className: "num"
  }, num(s.count)), /*#__PURE__*/React.createElement("td", {
    style: {
      fontFamily: "monospace"
    }
  }, s.sample)))))), report && report.empty && /*#__PURE__*/React.createElement(Container, {
    title: "Coverage preview",
    css: "margin-squeeze"
  }, /*#__PURE__*/React.createElement("p", {
    className: "module-empty"
  }, report.message || "No sales data to preview against yet.")), meta && (meta.history || []).length > 0 && /*#__PURE__*/React.createElement(Container, {
    title: "Previous versions",
    css: "margin-squeeze"
  }, (meta.history || []).map((h, i) => /*#__PURE__*/React.createElement("p", {
    key: i,
    style: {
      fontSize: 13,
      margin: "4px 0"
    }
  }, "v", (meta.version || 0) - i - 1, " \u2014 ", (h && h.lineRules || []).length, " rules, pairing ", h && h.pairing && h.pairing.strategy || "same_order", " ", /*#__PURE__*/React.createElement("button", {
    className: "rep-state-chip",
    onClick: () => revert(h)
  }, "Load into editor")))));
}